import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Save, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { PatientResponse, PatientUpdate } from "@/lib/types";
import { usePatientsStore } from "@/stores/patientsStore";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const PatientEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: patientId } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PatientUpdate>({
    first_name: "",
    last_name: "",
    preferred_name: null,
    phone_number: null,
    email: null,
    address: null,
    date_of_birth: null,
    gender: null,
    tags: null,
  });
  const [newTag, setNewTag] = useState("");

  const { getPatient, updatePatient } = usePatientsStore();

  useEffect(() => {
    if (!patientId) return;

    const fetchPatientData = async () => {
      try {
        // First try to get patient from store
        const storedPatient = getPatient(patientId);

        if (storedPatient) {
          // Initialize form with patient data
          setFormData({
            first_name: storedPatient.first_name,
            last_name: storedPatient.last_name,
            preferred_name: storedPatient.preferred_name || null,
            phone_number: storedPatient.phone_number || null,
            email: storedPatient.email || null,
            address: storedPatient.address || null,
            date_of_birth: storedPatient.date_of_birth || null,
            gender: storedPatient.gender || null,
            tags: storedPatient.tags || null,
          });
          setIsLoading(false);
        } else {
          // If not in store, fetch from API
          const patientData = await api.getPatient(patientId);
          setFormData({
            first_name: patientData.first_name,
            last_name: patientData.last_name,
            preferred_name: patientData.preferred_name || null,
            phone_number: patientData.phone_number || null,
            email: patientData.email || null,
            address: patientData.address || null,
            date_of_birth: patientData.date_of_birth || null,
            gender: patientData.gender || null,
            tags: patientData.tags || null,
          });
          setIsLoading(false);
        }
      } catch (err: any) {
        console.error("Error fetching patient:", err);
        setError(err?.response?.data?.detail || "Failed to load patient data");
        setIsLoading(false);
      }
    };

    fetchPatientData();
  }, [patientId, getPatient]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value || null, // Convert empty strings to null
    }));
  };

  const handleAddTag = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTag.trim()) return;

    setFormData((prev) => ({
      ...prev,
      tags: [...(prev.tags || []), newTag.trim()],
    }));
    setNewTag("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags ? prev.tags.filter((tag) => tag !== tagToRemove) : null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patientId) return;

    setIsSaving(true);
    try {
      // Update API
      await api.updatePatient(patientId, formData);

      // Update store - convert nulls to undefined to satisfy TypeScript
      const storeUpdate = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          acc[key as keyof PatientUpdate] = value === null ? undefined : value;
          return acc;
        },
        {} as Partial<PatientResponse>
      );

      updatePatient(patientId, storeUpdate);

      toast({
        title: "Success",
        description: "Patient information updated successfully.",
      });

      // Navigate back to patient detail
      navigate(`/patients/${patientId}`);
    } catch (err: any) {
      console.error("Error updating patient:", err);
      toast({
        title: "Error",
        description:
          err?.response?.data?.detail ||
          "Failed to update patient information.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="m-4 space-y-6 md:m-8">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-16 w-full" />
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-8">
        <Button
          variant="outline"
          className="mb-6 flex items-center gap-2"
          onClick={() => navigate(`/patients/${patientId}`)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="m-4 space-y-6 md:m-8">
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => navigate(`/patients/${patientId}`)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Patient Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name*</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name*</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferred_name">Preferred Name</Label>
                <Input
                  id="preferred_name"
                  name="preferred_name"
                  value={formData.preferred_name || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select
                  value={formData.gender || ""}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, gender: value }))
                  }
                >
                  <SelectTrigger id="gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Male</SelectItem>
                    <SelectItem value="F">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number</Label>
                <Input
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags && formData.tags.length > 0 ? (
                    formData.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        className="flex items-center gap-1"
                        variant="secondary"
                      >
                        {tag}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => handleRemoveTag(tag)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      No tags
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag..."
                  />
                  <Button
                    type="button"
                    onClick={handleAddTag}
                    variant="outline"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isSaving}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientEditPage;
